import { NgClass } from '@angular/common'
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { combineLatest, distinctUntilChanged, map, merge, mergeMap, of, takeUntil } from 'rxjs'

import { DotFlashingComponent, ResponsiveComponent } from '@nx-superprep/shared'
import { DateOnly, log, toUniqueNonNullableArray } from '@nx-superprep/utils'

import { UserSelection, _defaultDrawerStates } from '../@shared'

import { DrawerComponent, DrawerState } from "../drawer/drawer.component"

import {
  PerformanceService,
  LibraryService,
  PerformanceStats,
  PerformanceStatName,
  isSamePerformanceStatRecordArray,
  isSamePerformanceChartRecordArray,
  ModuleStats,
  ModuleTemplate,
  NavigationState,
  LongtailService
} from '@nx-superprep/backend/firebase'
import { LessonTile } from '@nx-superprep/study'
import { isEqual } from 'lodash-es'
import { isSameDrawerState } from '../drawer'
import { SearchState } from '@nx-superprep/shared'
import { ActivatedRoute } from '@angular/router'

export interface PerformanceNavigation {
  navigation: NavigationState,
  parent?: PerformanceNavigation,
  selection?: PerformanceSelection,
}
export interface PerformanceSelection extends UserSelection {
  id?: string,
  lesson?: string | undefined
  active?: string
}
@Component({
    selector: 'pop-ui-performance',
    standalone: true,
    templateUrl: './performance.component.html',
    styleUrls: ['./performance.component.scss'],
    imports: [NgClass, DrawerComponent, DotFlashingComponent]
})
export class PerformanceComponent extends ResponsiveComponent implements OnInit, OnDestroy {
  isLoading = true

  private _moduleId: string | undefined = undefined;
  @Input()
  get moduleId() { return this._moduleId }
  set moduleId(value){
    if (this._moduleId === value) { return }
    this._moduleId = value
    this.moduleIdChange.emit(value)
  }
  @Output()
  moduleIdChange = new EventEmitter<string>()

  protected override handleSearchChange(value: SearchState, previousSearch: SearchState): void {
    const isNew = (value.selected && value.selected !== previousSearch.selected)
    value.visible = !this.drawerState.contentState.selected
    if (isNew) {
      const found = this.drawerState.contentState.tiles?.find(t => t.title === value.selected)
      log.debug(`${value.selected} ${found ? '':'not '}found`)
      if (found?.id) {
        const element = document.getElementById(found.id)
        element?.scrollIntoView({ behavior:'smooth', block: 'center' })
        element?.click()
        this.search.selected = undefined
      }
    }
  }

  private readonly _performanceStats: DrawerState = _defaultDrawerStates('performance-stats')

  private readonly _defaultState: PerformanceNavigation = {
    selection: { title: 'Performance' },
    navigation: { current: { route: 'performance', type: 'root' }},
  }

  private _state: PerformanceNavigation = this._defaultState
  @Input()
  get state() { return this._state; }
  set state(value) {
    this._state = value
    const didChange = this.state.selection?.lesson !== this.drawerState.contentState.selected
    this.state.selection = { ...this.state.selection, didChange }
    if (this.state.selection.didChange) {
      this.state.selection.didChange = false
      this.moduleId = undefined
      this.updateData()
    }
    this.performanceService.navigationState = this.state
  }
  @Output() stateChange = new EventEmitter<PerformanceNavigation>()

  @Input() scroll = false

  private _drawerState: DrawerState = _defaultDrawerStates('performance-stats')
  @Input()
  get drawerState() {
    return this._drawerState
  }
  set drawerState(value) {
    const parent = {
      ...this.state,
      navigation: {...this.state.navigation},
    }
    if (this.state.selection?.lesson !== value.contentState.selected) {
      this.state.selection = { title: this.title, lesson: value.contentState.selected, didChange: true }
      this.state.navigation.current = { route: 'graph', type: 'back', title: value.contentState.selected }
      this.state.parent = parent
    }
    if (this.state.selection?.didChange) {
      this.state.selection.didChange = false
      const moduleId = this.getModuleId(value.contentState.selected, value.contentState.tiles)
      this.updateData(moduleId)
      this.longtailService.logPageView('performance', `${window.location.href}/performance`, `/performance/${this.drawerState.contentState.lesson?.id ?? this.drawerState.contentState.selected}`)
    }
  }
  @Output() drawerStateChange = new EventEmitter<DrawerState>()

  get title() { return this.state.selection?.lesson ?? 'Performance' }

  select(value: LessonTile|PerformanceStats) {
    if ('id' in value) {
      this.drawerState.contentState.selected = undefined
      this.drawerState.contentState.lesson = value as LessonTile
      this.performanceService.logPerformanceEvent(this.drawerState.contentState.lesson.id)
    } else {
      this.drawerState.contentState.lesson = undefined
      this.drawerState.contentState.selected = (value as PerformanceStats).name.toTitleCase()
      this.performanceService.logPerformanceEvent(this.drawerState.contentState.selected)
    }
    return Promise.resolve(true)
  }

  getModuleId(title?: string, tiles = this.drawerState.contentState.tiles) {
    return title ? ['retention', 'correct', 'studied'].includes(title.toLowerCase()) ? 'Overall' : tiles?.find(t => t.title === title)?.id : undefined
  }

  getDrawerForCharts(moduleId: string|undefined) {
    const selected = this.state.selection?.lesson?.toLowerCase() as PerformanceStatName
    const _drawerState: DrawerState = _defaultDrawerStates('performance-charts')
    return this.performanceService.getModuleCharts(moduleId, selected)
    .pipe(
      distinctUntilChanged(isSamePerformanceChartRecordArray),
      map(charts => {
        _drawerState.contentState.selected = this.state.selection?.lesson
        _drawerState.contentState.charts = charts
        _drawerState.contentState.date = new DateOnly()
        _drawerState.contentState.isLoaded = true
        const activeTab = (_drawerState.headerState.tabs??[])[0]
        if (activeTab) { _drawerState.headerState.activeTab = activeTab.label }
        return _drawerState
      })
    )
  }

  getDrawerForStats(lesson?: string) {
    const _drawerState: DrawerState = _defaultDrawerStates('performance-stats')
    return this.performanceService.getGlobalStats()
    .pipe(
      distinctUntilChanged(isSamePerformanceStatRecordArray),
      map((stats) => {
        _drawerState.contentState.selected = lesson
        _drawerState.contentState.stats = stats
        _drawerState.contentState.date = new DateOnly()
        _drawerState.contentState.isLoaded = true
        const activeTab = (_drawerState.headerState.tabs ?? [])[0]
        if (activeTab) { _drawerState.headerState.activeTab = activeTab.label }
        const completed = stats.find(s => s.range === 'today')?.stats.find((s: PerformanceStats) => s.name === 'completed')
        if (completed) { completed.value = (100 * this.performanceService.completedToday).toFixed(0) }
        return _drawerState
      })
    )
  }

  getLessonTiles() {
    const result = this._drawerState.contentState.tiles??[] as LessonTile[]
    return this.performanceService.fetchModuleStats(true)
    .pipe(
      distinctUntilChanged(isEqual),
      mergeMap((response: ModuleStats[]) => {
        const moduleIds = toUniqueNonNullableArray(response.map(m => m.moduleId))
        return (moduleIds.length === 0)
        ? of(result)
        : merge(of(result),this.libraryService.getModules(moduleIds)
          .pipe(
            distinctUntilChanged(isEqual),
            map((modules: ModuleTemplate[]) =>
              modules.filter(m => m.id && moduleIds.includes(m.id)).map(t => {
                const found = response.find(s => (s.moduleId === t.id))
                return ({ ...t, progress: found ? found.progress : 0 } as unknown as LessonTile)
              })
            ),
          ))
      }),
    )
  }

  override ngOnDestroy() {
    super.ngOnDestroy()
    this.moduleId = undefined
  }

  readonly route = inject(ActivatedRoute)

  override ngOnInit() {
    super.ngOnInit()
    // Bind parameters to inputs
    this.moduleId = this.route.snapshot.paramMap.get('moduleId') ?? undefined
    this.stateChange.emit(this._defaultState)
    this.updateData()
  }

  updateData(item?: string) {
    this.isLoading = (this._drawerState.contentState.tiles??[]).length === 0
    this.onCancel.next()
    const source = combineLatest([item ? this.getDrawerForCharts(item) : this.getDrawerForStats(), this.getLessonTiles()])
    // timeout to handle cases where tiles might be empty and no further updates are expected
    const loadingTimeout = setTimeout(() => { this.isLoading = false }, 3000)

    source.pipe(
      takeUntil(merge(this.onDestroy, this.onCancel)),
      distinctUntilChanged(isEqual)
    )
    .subscribe(([drawer, tiles]: [DrawerState, LessonTile[]]) => {
      if (!drawer) { return } // If drawer data is not available, return
      drawer.contentState.tiles = tiles

      // Check if the drawer has changed
      const _isSameContent = isSameDrawerState(drawer, this.drawerState)
      if (!_isSameContent) {
        // If content has changed, update the drawer state
        const _updatedDrawerState: DrawerState = {
          type: item ? 'performance-charts' : 'performance-stats',
          contentState: {
              ...this.drawerState.contentState,
              ...drawer.contentState
          },
          headerState: {
              ...this.drawerState.headerState,
              ...drawer.headerState
          }
        }
        if (!isEqual(this._drawerState, _updatedDrawerState)) {
          this._drawerState = _updatedDrawerState
          // emit change for navigation update
          this.stateChange.emit(this.state)
        }
      }

      if (!item) {
        // Update additional properties and emit changes
        this._drawerState.contentState.tiles = tiles
        this._drawerState.contentState.date = new DateOnly()
        this._drawerState.contentState.select = this.select.bind(this)
        this._drawerState.contentState.isLoaded = true
        const activeTab = ( this._drawerState.headerState.tabs ?? [])[0]
        if (activeTab) { this._drawerState.headerState.activeTab = activeTab.label }
        this.drawerStateChange.emit(this._drawerState)
      }

      if (tiles.length > 0) {
        this.isLoading = false
        clearTimeout(loadingTimeout)
      }

      this.search.keywords = toUniqueNonNullableArray(tiles.map(l => l.title))

      if (this.moduleId && (this.drawerState.contentState.tiles??[]).length > 0) {
        setTimeout(() => {
          const found = this.drawerState.contentState.tiles?.find(t => t.id === this.moduleId)
          log.debug(`${this.moduleId} ${found ? '':'not '}found`)
          if (found?.id) {
            const element = document.getElementById(found.id)
            element?.click()
          }
        }, 0)
      }
    })
  }

  override get rootClassName() { return `${super.rootClassName}${this.scroll ? ' scrollable': ''}` }

  readonly longtailService = inject(LongtailService)
  readonly libraryService = inject(LibraryService)
  readonly performanceService = inject(PerformanceService)

  constructor() {
    super()
    super.rootClassName = 'performance-root-class-name'
  }
}
